/** Semantic role — drives the node's border colour and focus glow. */
export type NodeRole = 'source' | 'agent' | 'engine' | 'graph' | 'output'

export interface GraphNode {
  id: string
  /** Role key; sets .tl-node--<role> which defines --kind-color. */
  role: NodeRole
  /** Uppercase micro-label rendered above the name. */
  tag: string
  name: string
  /** Percentage coordinates on the 100×100 canvas. */
  x: number
  y: number
  /** Optional overrides below 640px, where a wide row will not fit. */
  xMobile?: number
  yMobile?: number
  /** Always-lit centrepiece: heavier left border plus a pulsing halo. */
  primary?: boolean
}

export interface GraphEdge {
  from: string
  to: string
  /** Conversational link: solid and grey, never dashed, no tracer dots. */
  activeOnly?: boolean
  /** "Shipped" link: solid accent stroke, but tracers still flow. */
  solid?: boolean
  /** Edge between members of a group — the composite card covers it. */
  internal?: boolean
}

/** One beat of the reveal: light these nodes, focus one, dwell `ms`. */
export interface GraphStep {
  show: string[]
  focus: string
  ms: number
}

export interface GraphGroupItem {
  id: string
  name: string
  role: string
  color: string
}

/** A composite card standing in for several nodes (two engines shown as one). */
export interface GraphGroup {
  ids: string[]
  tag: string
  primary?: boolean
  items: GraphGroupItem[]
}

export interface GraphScene {
  nodes: GraphNode[]
  edges: GraphEdge[]
  schedule: GraphStep[]
  groups?: GraphGroup[]
  /** Stagger between scenes so switching tabs does not look cloned. */
  startOffsetMs: number
}

/** Effective position of a node, resolving mobile overrides. */
export function nodePos(n: GraphNode, isMobile: boolean): { x: number; y: number } {
  return {
    x: isMobile && n.xMobile !== undefined ? n.xMobile : n.x,
    y: isMobile && n.yMobile !== undefined ? n.yMobile : n.y,
  }
}

/**
 * Where an edge should actually terminate. Members of a group collapse to the
 * group's centre of mass, so a connector lands on the composite card's edge
 * rather than at a ghost position underneath it.
 */
export function anchorFor(
  id: string,
  nodes: GraphNode[],
  groups: GraphGroup[] | undefined,
  isMobile: boolean,
): { x: number; y: number } | null {
  const group = groups?.find((g) => g.ids.includes(id))
  const lookup = (nid: string) => nodes.find((n) => n.id === nid)

  if (group) {
    const members = group.ids.map(lookup).filter((n): n is GraphNode => Boolean(n))
    if (members.length === 0) return null
    let sx = 0
    let sy = 0
    for (const m of members) {
      const p = nodePos(m, isMobile)
      sx += p.x
      sy += p.y
    }
    return { x: sx / members.length, y: sy / members.length }
  }

  const node = lookup(id)
  return node ? nodePos(node, isMobile) : null
}
